"use client";

import type { CSSProperties } from "react";
import {
	ArtistIcon,
	EmptyState,
} from "@/components/design-system/ArtistDesignSystem";
import type { LibraryTrack } from "./library-data";

export interface WorksGridViewProps {
	readonly tracks: readonly LibraryTrack[];
	readonly onTrackTap?: (track: LibraryTrack, index: number) => void;
	readonly emptyTitle: string;
	readonly emptyMessage: string;
}

const gridStyle: CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
	gap: 1,
	width: "100%",
};

/**
 * WorksGridView — 3-column square grid with a like-count overlay on each
 * tile. Mirrors `WorksGridView` on iOS (`ThreeColumnSquareGrid` + bottom
 * gradient + heart/count overlay). Used for both the 作品 and 好听 tabs.
 */
export function WorksGridView({
	tracks,
	onTrackTap,
	emptyTitle,
	emptyMessage,
}: Readonly<WorksGridViewProps>) {
	if (tracks.length === 0) {
		return (
			<EmptyState
				className="library-empty"
				title={emptyTitle}
				message={emptyMessage}
				symbol={<ArtistIcon name="music" size={36} />}
			/>
		);
	}

	return (
		<ul className="library-works-grid" style={gridStyle}>
			{tracks.map((track, index) => (
				<li key={track.id} className="library-works-grid__item">
					<WorkTile track={track} onTap={() => onTrackTap?.(track, index)} />
				</li>
			))}
		</ul>
	);
}

function WorkTile({
	track,
	onTap,
}: Readonly<{ track: LibraryTrack; onTap: () => void }>) {
	const tileStyle: CSSProperties = {
		position: "relative",
		display: "block",
		width: "100%",
		aspectRatio: "1 / 1",
		border: 0,
		padding: 0,
		background: "var(--ds-surface)",
		cursor: "pointer",
		overflow: "hidden",
	};

	const overlayStyle: CSSProperties = {
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 0,
		height: 40,
		background: "linear-gradient(to bottom, transparent, rgb(0 0 0 / 0.6))",
		pointerEvents: "none",
	};

	const countStyle: CSSProperties = {
		position: "absolute",
		left: 6,
		bottom: 6,
		display: "flex",
		alignItems: "center",
		gap: 4,
		fontSize: "0.72rem",
		color: "var(--ds-on-media-primary)",
		fontVariantNumeric: "tabular-nums",
	};

	return (
		<button
			type="button"
			style={tileStyle}
			onClick={onTap}
			aria-label={track.title}
		>
			<WorkArtwork track={track} />
			<div style={overlayStyle} aria-hidden="true" />
			<div style={countStyle} aria-hidden="true">
				<ArtistIcon name="heart" size={12} strokeWidth={2} />
				<span>{formatCount(track.likeCount)}</span>
			</div>
		</button>
	);
}

function WorkArtwork({ track }: Readonly<{ track: LibraryTrack }>) {
	if (track.artworkURL === null) {
		return (
			<span
				style={{
					display: "grid",
					placeItems: "center",
					width: "100%",
					height: "100%",
					color: "var(--ds-icon-secondary)",
					background: "var(--ds-surface)",
				}}
				aria-hidden="true"
			>
				<ArtistIcon name="music" size={40} />
			</span>
		);
	}

	const imgStyle: CSSProperties = {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		display: "block",
	};
	return (
		// biome-ignore lint/performance/noImgElement: track artwork with dynamic inline styling; next/image not feasible on static export
		<img
			src={track.artworkURL}
			alt={track.title}
			style={imgStyle}
			loading="lazy"
		/>
	);
}

function formatCount(value: number): string {
	if (value >= 10000) return `${(value / 10000).toFixed(1)}w`;
	if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
	return value.toLocaleString("zh-CN");
}
